import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { Lock, Unlock, Edit2, Trash2, Save, X, Building2 } from 'lucide-react';
import './AdminPage.css';

const AdminPage = () => {
    const [pin, setPin] = useState('');
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [authError, setAuthError] = useState('');
    const [editingId, setEditingId] = useState(null);
    const [editData, setEditData] = useState({});
    const queryClient = useQueryClient();

    const { data: vacations, isLoading } = useQuery(
        'adminVacations',
        async () => {
            const response = await axios.get(`${API_BASE_URL}/vacations`);
            return response.data;
        },
        {
            enabled: isAuthenticated,
            staleTime: 30 * 1000,
        }
    );

    const { data: departments } = useQuery(
        'adminDepartments',
        async () => {
            const response = await axios.get(`${API_BASE_URL}/departments`);
            return response.data;
        },
        { enabled: isAuthenticated }
    );

    const { data: departmentLimits } = useQuery(
        'departmentLimits',
        async () => {
            const response = await axios.get(`${API_BASE_URL}/department-settings/all`);
            return response.data;
        },
        { enabled: isAuthenticated }
    );

    const limitsByName = useMemo(() => {
        const map = new Map();
        (departmentLimits || []).forEach((row) => {
            map.set(row.department_name, String(row.max_vacations_per_week));
        });
        return map;
    }, [departmentLimits]);

    const [editedLimits, setEditedLimits] = useState({});

    const saveLimitMutation = useMutation(
        async ({ department_name, max_vacations_per_week }) => {
            const response = await axios.put(`${API_BASE_URL}/department-settings`, {
                department_name,
                max_vacations_per_week,
                pin,
            });
            return response.data;
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries('departmentLimits');
                queryClient.invalidateQueries('departments');
            },
        }
    );

    const verifyMutation = useMutation(
        async (pinCode) => {
            const response = await axios.post(`${API_BASE_URL}/admin/verify`, { pin: pinCode });
            return response.data;
        },
        {
            onSuccess: () => {
                setIsAuthenticated(true);
                setAuthError('');
            },
            onError: () => {
                setAuthError('Неверный пин-код');
            }
        }
    );

    const updateMutation = useMutation(
        async ({ id, data }) => {
            const response = await axios.put(`${API_BASE_URL}/vacations/${id}`, { ...data, pin });
            return response.data;
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries('adminVacations');
                setEditingId(null);
                setEditData({});
            }
        }
    );

    const deleteMutation = useMutation(
        async (id) => {
            await axios.delete(`${API_BASE_URL}/vacations/${id}`, { data: { pin } });
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries('adminVacations');
            }
        }
    );

    const handlePinSubmit = (e) => {
        e.preventDefault();
        verifyMutation.mutate(pin);
    };

    const handleEdit = (vacation) => {
        setEditingId(vacation.id);
        setEditData({
            full_name: vacation.full_name,
            department_name: vacation.department_name || '',
            start_date: vacation.start_date.split('T')[0],
            end_date: vacation.end_date.split('T')[0]
        });
    };

    const handleSave = (id) => {
        updateMutation.mutate({ id, data: editData });
    };

    const handleDelete = (id) => {
        if (window.confirm('Удалить этот отпуск?')) {
            deleteMutation.mutate(id);
        }
    };

    const handleCancel = () => {
        setEditingId(null);
        setEditData({});
    };

    if (!isAuthenticated) {
        return (
            <div className="admin-page">
                <div className="pin-container">
                    <div className="pin-icon">
                        <Lock size={40} />
                    </div>
                    <h1>Панель администратора</h1>
                    <p>Введите пин-код для доступа</p>

                    <form onSubmit={handlePinSubmit} className="pin-form">
                        <input
                            type="password"
                            value={pin}
                            onChange={(e) => setPin(e.target.value)}
                            placeholder="Пин-код"
                            maxLength={10}
                            autoFocus
                        />
                        {authError && <div className="error-message">{authError}</div>}
                        <button 
                            type="submit" 
                            className="btn btn-primary"
                            disabled={verifyMutation.isLoading}
                        >
                            {verifyMutation.isLoading ? 'Проверка...' : (
                                <>
                                    <Unlock size={18} />
                                    Войти
                                </>
                            )}
                        </button>
                    </form>
                </div>
            </div>
        );
    }

    return (
        <div className="admin-page authenticated">
            <div className="settings-panel">
                <div className="settings-header">
                    <Building2 size={20} />
                    <h2>Отделения</h2>
                </div>
                <div className="departments-list">
                    {departments?.map(dep => (
                        <div key={dep.id} className="department-item">
                            <span>{dep.name}</span>
                            <div className="department-limit">
                                <span className="department-limit-label">Максимум отпусков на одну дату</span>
                                <div className="department-limit-row">
                                    <input
                                        type="number"
                                        min="1"
                                        max="99"
                                        className="settings-input"
                                        value={
                                            editedLimits[dep.name] ??
                                            limitsByName.get(dep.name) ??
                                            '2'
                                        }
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            setEditedLimits((prev) => ({ ...prev, [dep.name]: value }));
                                        }}
                                    />
                                    <button
                                        className="btn btn-primary btn-sm"
                                        onClick={() => {
                                            const value =
                                                editedLimits[dep.name] ??
                                                limitsByName.get(dep.name) ??
                                                '2';
                                            saveLimitMutation.mutate({
                                                department_name: dep.name,
                                                max_vacations_per_week: value,
                                            });
                                        }}
                                        disabled={saveLimitMutation.isLoading}
                                    >
                                        <Save size={14} /> Сохранить
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                    {(!departments || departments.length === 0) && (
                        <p className="no-departments">Нет отделений</p>
                    )}
                </div>
            </div>

            {isLoading ? (
                <div className="loading-container">
                    <div className="spinner"></div>
                    <p>Загрузка...</p>
                </div>
            ) : (
                <div className="vacations-list">
                    {!vacations || vacations.length === 0 ? (
                        <div className="empty-state">
                            <p>Нет заявок на отпуск</p>
                        </div>
                    ) : (
                        <table className="vacations-table">
                            <thead>
                                <tr>
                                    <th>ФИО</th>
                                    <th>Отделение</th>
                                    <th>Начало</th>
                                    <th>Окончание</th>
                                    <th>Действия</th>
                                </tr>
                            </thead>
                            <tbody>
                                {vacations.map(vacation => (
                                    <tr key={vacation.id}>
                                        {editingId === vacation.id ? (
                                            <>
                                                <td>
                                                    <input
                                                        type="text"
                                                        value={editData.full_name}
                                                        onChange={(e) => setEditData({ ...editData, full_name: e.target.value })}
                                                        className="edit-input"
                                                    />
                                                </td>
                                                <td>
                                                    <select
                                                        value={editData.department_name || ''}
                                                        onChange={(e) => setEditData({ ...editData, department_name: e.target.value || null })}
                                                        className="edit-input"
                                                    >
                                                        <option value="">—</option>
                                                        {departments?.map(dep => (
                                                            <option key={dep.id} value={dep.name}>{dep.name}</option>
                                                        ))}
                                                    </select>
                                                </td>
                                                <td>
                                                    <input
                                                        type="date"
                                                        value={editData.start_date}
                                                        onChange={(e) => setEditData({ ...editData, start_date: e.target.value })}
                                                        className="edit-input"
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        type="date"
                                                        value={editData.end_date}
                                                        onChange={(e) => setEditData({ ...editData, end_date: e.target.value })}
                                                        className="edit-input"
                                                    />
                                                </td>
                                                <td className="actions-cell">
                                                    <button 
                                                        className="btn btn-primary btn-sm"
                                                        onClick={() => handleSave(vacation.id)}
                                                        disabled={updateMutation.isLoading}
                                                    >
                                                        <Save size={14} />
                                                    </button>
                                                    <button 
                                                        className="btn btn-secondary btn-sm"
                                                        onClick={handleCancel}
                                                    >
                                                        <X size={14} />
                                                    </button>
                                                </td>
                                            </>
                                        ) : (
                                            <>
                                                <td className="name-cell">{vacation.full_name}</td>
                                                <td>{vacation.department_name || '—'}</td>
                                                <td>{format(new Date(vacation.start_date), 'dd.MM.yyyy', { locale: ru })}</td>
                                                <td>{format(new Date(vacation.end_date), 'dd.MM.yyyy', { locale: ru })}</td>
                                                <td className="actions-cell">
                                                    <button 
                                                        className="btn btn-secondary btn-sm"
                                                        onClick={() => handleEdit(vacation)}
                                                    >
                                                        <Edit2 size={14} />
                                                    </button>
                                                    <button 
                                                        className="btn btn-danger btn-sm"
                                                        onClick={() => handleDelete(vacation.id)}
                                                        disabled={deleteMutation.isLoading}
                                                    >
                                                        <Trash2 size={14} />
                                                    </button>
                                                </td>
                                            </>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}
        </div>
    );
};

export default AdminPage;
